import React, {useState, useMemo, useEffect} from 'react';
import { ChevronLeft, Calendar, CreditCard, Receipt, Store, MessageSquare, X, ChevronDown, Wallet, Image as ImageIcon, Camera, Coins, Edit2, Check, Sparkles } from 'lucide-react';
import {
    changeStat,
    getBillingList,
    postPay,
    uploadLogo
} from "../../api/system/system.api";
import {fileUpload} from "../../api/file/file.api";
import StickerGeneratorForm from "../form/StickerGeneratorForm";
import {Tag} from "omoplay";

const generateMonthOptions = () => {
    const options = [];
    const start = new Date(2024, 0); // 시작점: 2024년 1월
    const end = new Date();

    while (end >= start) {
        const year = end.getFullYear();
        const month = String(end.getMonth() + 1).padStart(2, '0');

        options.push(`${year}-${month}`);
        end.setMonth(end.getMonth() - 1);
    }
    return options;
};

export default function BillingSystem() {
    const [channels, setChannels] = useState([])
    const monthOptions = useMemo(() => generateMonthOptions(), []);

    const [selectedChannel, setSelectedChannel] = useState(null);
    const [viewDate, setViewDate] = useState(monthOptions[1]);

    const [isPayModalOpen, setIsPayModalOpen] = useState(false);
    const [payingChannel, setPayingChannel] = useState(null);
    const [paymentHistory, setPaymentHistory] = useState([])

    // 로고 및 스티커 이미지 업로드 상태
    const [logoPreview, setLogoPreview] = useState(null);
    const [stickerPreview, setStickerPreview] = useState(null);
    const [showStickerGenerator, setShowStickerGenerator] = useState(false)

    // 카드 인라인 수정 상태 관리
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState("");

    const [payingPrice, setPayingPrice] = useState(0)

    // 조회는 **버튼으로만** 실행한다(2026-08-04).
    //
    // 종전에는 마운트 시 + viewDate 변경 시마다 자동으로 getBillingList 를 호출했다.
    // 이 API 는 전 채널의 월 청구액을 한 번에 계산하는 무거운 집계라, **화면에 들어오기만 해도**
    // 운영 DB 에 큰 부하가 걸렸다(§2-K Cloud SQL CPU 100% 인시던트와 같은 성격).
    // 게다가 월을 바꾸는 중간 단계마다 재조회가 나갔다.
    // → 수익 대시보드와 동일하게 '조회' 버튼 게이트로 통일한다.
    const [queried, setQueried] = useState(false);
    const [listLoading, setListLoading] = useState(false);

    const loadBillingList = async () => {
        setListLoading(true);
        try {
            const {data} = await getBillingList(viewDate);
            setChannels(data);
            setPaymentHistory(data.flatMap(bill => bill.histories));
            setQueried(true);
        } finally {
            setListLoading(false);
        }
    };

    // 월을 바꾸면 이전 달 결과를 그대로 두지 않는다 — 화면의 월 표시와 숫자가 어긋나면 오독하기 쉽다.
    useEffect(() => {
        setChannels([]);
        setPaymentHistory([]);
        setSelectedChannel(null);
        setQueried(false);
    }, [viewDate]);

    useEffect(() => {
        if (selectedChannel) {
            setLogoPreview(selectedChannel.logoUrl || null);
            setStickerPreview(selectedChannel.stickerUrl || null);
            // 채널 변경 시 수정 모드 초기화
            setEditingField(null);
        }
    }, [selectedChannel]);

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            fileUpload(file).then(({data}) => {
                uploadLogo(selectedChannel.channelId, data).then(() => {
                    setLogoPreview(data);
                    e.target.value = null
                })
            })
        }
    };

    // 카드 값 수정 시작
    const handleEditClick = (field, currentValue) => {
        setEditingField(field);
        setEditValue(currentValue || 0);
    };

    // 카드 값 수정 저장
    const handleSaveEdit = () => {
        if (!editingField) return;
        changeStat(selectedChannel.channelId, selectedChannel.storeCount, selectedChannel.monthlyPrice, selectedChannel.unitPrice).then(() => {
            const numValue = Number(editValue);

            // selectedChannel 업데이트
            setSelectedChannel(prev => ({
                ...prev,
                [editingField]: numValue
            }));

            // 메인 channels 배열도 동기화
            setChannels(prev => prev.map(ch =>
                ch.channelId === selectedChannel.channelId
                    ? { ...ch, [editingField]: numValue }
                    : ch
            ));

            setEditingField(null);
        })
    };

    const handleCancelEdit = () => {
        setEditingField(null);
    };

    const ModernDateSelector = ({ value, onChange }) => {
        const [isOpen, setIsOpen] = useState(false);
        const [selectedYear, selectedMonth] = value.split('-');

        const groupedOptions = useMemo(() => {
            const groups = {};
            monthOptions.forEach(opt => {
                const [year, month] = opt.split('-');
                if (!groups[year]) groups[year] = [];
                groups[year].push(month);
            });
            return groups;
        }, []);

        return (
            <div className="relative">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-700 hover:border-gray-300 hover:shadow-sm transition-all focus:outline-none focus:ring-4 focus:ring-blue-50"
                >
                    <Calendar size={18} className="text-blue-500" />
                    <span>{`${selectedYear}년 ${selectedMonth}월`}</span>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

                        <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                            <div className="p-4 max-h-[400px] overflow-y-auto custom-scrollbar">
                                {Object.entries(groupedOptions)
                                    .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                                    .map(([year, months]) => (
                                        <div key={year} className="mb-5 last:mb-0">
                                            <div className="text-xs font-black text-gray-300 mb-3 pl-1 tracking-wider">{year}년</div>
                                            <div className="grid grid-cols-4 gap-2">
                                                {months.map(month => {
                                                    const optionValue = `${year}-${month}`;
                                                    const isSelected = optionValue === value;
                                                    return (
                                                        <button
                                                            key={optionValue}
                                                            onClick={() => {
                                                                onChange(optionValue);
                                                                setIsOpen(false);
                                                            }}
                                                            className={`py-2 rounded-xl text-sm font-bold transition-all duration-200 ${
                                                                isSelected
                                                                    ? 'bg-blue-600 text-white shadow-md shadow-blue-200 scale-105'
                                                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                                            }`}
                                                        >
                                                            {month.replace(/^0/, '')}월
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const handleManualPayment = () => {
        if (window.confirm(`정말 ${payingPrice}원을 결제 하시겠습니까?`)) {
            postPay(payingChannel.channelId, payingPrice).then(() => {
                setIsPayModalOpen(false);
                setPayingChannel(null);
            })
        }
    };

    const renderMainTable = () => (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">정산 및 청구 관리 (legacy)</h1>
                <div className="flex items-center gap-3">
                    <ModernDateSelector value={viewDate} onChange={setViewDate} />
                    <button
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50"
                        onClick={loadBillingList}
                        disabled={listLoading}
                        title="전 채널의 월 청구액을 계산합니다. 무거운 조회라 버튼을 눌렀을 때만 실행됩니다."
                    >
                        {listLoading ? '조회 중…' : '조회'}
                    </button>
                </div>
            </div>

            {!queried && (
                <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
                    <p className="text-gray-700 font-semibold">{viewDate} 청구 내역을 조회합니다.</p>
                    <p className="mt-2 text-sm text-gray-500">
                        전 채널의 월 청구액을 한 번에 계산하는 무거운 조회라, <b>조회 버튼을 눌렀을 때만</b> 실행됩니다.
                        <br />
                        (2026-07 이용분부터의 정산은 <b>정산 관리</b> 화면을 사용하세요.)
                    </p>
                </div>
            )}

            {queried && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
                <table className="w-full text-left text-sm text-gray-600">
                    <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-4 font-semibold text-gray-700">채널명</th>
                        <th className="px-6 py-4 font-semibold text-gray-700">결제수단</th>
                        <th className="px-6 py-4 font-semibold text-gray-700">상태</th>
                        <th className="px-6 py-4 font-semibold text-gray-700 text-right">기본 비용</th>
                        <th className="px-6 py-4 font-semibold text-gray-700 text-right">메시지 비용</th>
                        <th className="px-6 py-4 font-semibold text-gray-700 text-center">관리</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                    {channels.map((channel) => {
                        return (
                            <tr
                                key={channel.channelId}
                                onClick={() => setSelectedChannel(channel)}
                                className="hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                                <td className={`px-6 py-4 font-bold text-gray-900 ${!channel.isUsed && 'line-through'}`}>
                                    {
                                        !channel.isUsed && (<Tag color="error">미사용</Tag> )
                                    }
                                    {channel.channelName}
                                </td>
                                <td className="px-6 py-4">
                                    <span className="flex items-center gap-1.5">
                                      {channel.payMethod === 'CARD' ? <CreditCard size={14} /> : <Receipt size={14} />}
                                        {channel.payMethod === 'CARD' ? '카드' : '세금계산서'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                                        channel.hasPaid ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                                    }`}>
                                      {channel.hasPaid ? '결제됨' : '결제 필요'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-right font-medium">{channel.monthlyFee?.toLocaleString() || 0}원</td>
                                <td className="px-6 py-4 text-right font-medium">{channel.msgFee?.toLocaleString() || 0}원</td>
                                <td className="px-6 py-4 text-center">
                                    {
                                        channel.payMethod === 'CARD' && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPayingChannel(channel);
                                                    setIsPayModalOpen(true);
                                                    setPayingPrice((channel.monthlyFee || 0) + (channel.msgFee || 0))
                                                }}
                                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 shadow-md shadow-indigo-100 transition-all"
                                            >
                                                수동 결제
                                            </button>
                                        )
                                    }
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            )}
        </div>
    );

    // 수정 가능한 카드 렌더링을 위한 헬퍼 함수
    const renderEditableCard = (title, field, value, unit, IconComponent, iconColorClass) => {
        const isEditing = editingField === field;

        return (
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 group">
                <p className="text-xm font-bold text-gray-400 mb-2 uppercase tracking-tighter">{title}</p>
                <div className="flex items-end justify-between h-10">
                    {isEditing ? (
                        <div className="flex items-center gap-2 flex-1 mr-2">
                            <input
                                type="number"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveEdit();
                                    if (e.key === 'Escape') handleCancelEdit();
                                }}
                                className="w-full text-xl font-black text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                            />
                            <div className="flex flex-col gap-1">
                                <button onClick={handleSaveEdit} className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700">
                                    <Check size={14} strokeWidth={3} />
                                </button>
                                <button onClick={handleCancelEdit} className="p-1 bg-gray-100 text-gray-500 rounded hover:bg-gray-200">
                                    <X size={14} strokeWidth={3} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="flex items-center gap-2 cursor-pointer relative"
                            onClick={() => handleEditClick(field, value)}
                        >
                            <span className="text-xl font-black text-gray-800 border-b-2 border-transparent group-hover:border-blue-200 transition-colors">
                                {value?.toLocaleString() || 0}{unit}
                            </span>
                            <Edit2 size={16} className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity absolute -right-6 top-1" />
                        </div>
                    )}
                    {!isEditing && <IconComponent className={iconColorClass} size={32} />}
                </div>
            </div>
        );
    };

    const renderDetailView = () => {
        const filteredHistory = paymentHistory.filter((h) =>  h.channelId === selectedChannel.channelId);

        return (
            <div className="p-6 max-w-6xl mx-auto">
                <div className="flex justify-between items-center">
                    <button
                        onClick={() => setSelectedChannel(null)}
                        className="flex items-center text-gray-500 hover:text-gray-800 mb-6 transition-colors font-medium"
                    >
                        <ChevronLeft size={20} /> 정산 목록으로
                    </button>
                    <ModernDateSelector value={viewDate} onChange={setViewDate} />
                </div>

                <div className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-6">
                        <div className="relative group w-32 h-32 flex-shrink-0 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex items-center justify-center">
                            {logoPreview ? (
                                <img src={logoPreview} alt="로고 이미지" className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-gray-300 flex flex-col items-center">
                                    <Store size={32} className="mb-2" />
                                    <span className="text-[10px] font-bold">로고 없음</span>
                                </div>
                            )}
                            <label className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                <Camera size={24} className="text-white mb-1" />
                                <span className="text-white text-xs font-bold">이미지 첨부</span>
                                <input type="file" className="hidden" accept="image/png, image/jpeg" onChange={(e) => handleLogoUpload(e)} />
                            </label>
                        </div>

                        <div>
                            <h1 className="text-3xl font-black text-gray-900 mb-2">{selectedChannel.channelName}</h1>
                            <p className="text-gray-500 text-sm">신청일: {selectedChannel.createdAt}</p>
                        </div>
                    </div>

                </div>

                <div className="grid grid-cols-3 md:grid-cols-3 gap-4 mb-8">
                    {/* 수정 가능한 카드 영역 */}
                    {renderEditableCard("객단가", "unitPrice", selectedChannel.unitPrice, "원", Coins, "text-purple-100")}
                    {renderEditableCard("연결된 매장", "storeCount", selectedChannel.storeCount, "개", Store, "text-blue-100")}
                    {selectedChannel.monthlyPrice ?
                        renderEditableCard(`월 이용료`, "monthlyFee", selectedChannel.monthlyPrice, "원", Calendar, "text-blue-100") :

                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <p className="text-sm font-bold text-gray-400 mb-2 uppercase tracking-tighter">고객 획득 비용</p>
                            <div className="flex items-end justify-between h-10">
                                <span className="text-xl font-black text-gray-800 mt-auto">275원</span>
                                <CreditCard className="text-green-100" size={32} />
                            </div>
                        </div>
                    }

                    {/* 고정 (수정 불가) 카드 영역 */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-sm font-bold text-gray-400 mb-2 uppercase tracking-tighter">{viewDate}월 기본 이용료</p>
                        <div className="flex items-end justify-between h-10">
                            <span className="text-xl font-black text-gray-800 mt-auto">{selectedChannel.monthlyFee?.toLocaleString() || 0}원</span>
                            <CreditCard className="text-green-100" size={32} />
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-sm font-bold text-gray-400 mb-2 uppercase tracking-tighter">{viewDate}월 메시지 이용료</p>
                        <div className="flex items-end justify-between h-10">
                            <span className="text-xl font-black text-gray-800 mt-auto">{selectedChannel.msgFee?.toLocaleString() || 0}원</span>
                            <MessageSquare className="text-yellow-100" size={32} />
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-indigo-100 bg-indigo-50/30">
                        <p className="text-sm font-bold text-indigo-400 mb-2 uppercase tracking-tighter">{viewDate}월 총 합계</p>
                        <div className="flex items-end justify-between h-10">
                            <span className="text-xl font-black text-indigo-600 mt-auto">{((selectedChannel.monthlyFee || 0) + (selectedChannel.msgFee || 0)).toLocaleString()}원</span>
                            <Wallet className="text-indigo-200" size={32} />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-black text-gray-800">결제 내역 상세</h2>
                    </div>

                    <table className="w-full text-left text-sm">
                        <thead className="text-gray-400 border-b border-gray-100">
                        <tr>
                            <th className="pb-4 font-bold uppercase">결제일</th>
                            <th className="pb-4 font-bold uppercase">결제 금액</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                        {filteredHistory.length > 0 ? (
                            filteredHistory.map((history) => (
                                <tr key={history.id} className="group">
                                    <td className="py-5 text-gray-500">{history.paidDate}</td>
                                    <td className="py-5 font-black text-gray-900">{history.balance.toLocaleString()}원</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="py-12 text-center">
                                    <div className="flex flex-col items-center text-gray-300">
                                        <Calendar size={48} className="mb-2 opacity-20" />
                                        <p className="font-bold">결제 내역이 없습니다.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-black text-gray-800">스티커 이미지</h2>
                    </div>
                    {/* 헤더 (클릭 영역) */}
                    <button
                        onClick={() => setShowStickerGenerator(!showStickerGenerator)}
                        className="w-full px-5 py-4 flex items-center justify-between bg-gray-200 hover:bg-gray-100 rounded-lg transition-colors duration-200 focus:outline-none mb-2"
                    >
                        <div className="flex items-center space-x-2 text-gray-800">
                            <Sparkles className="w-5 h-5 text-indigo-500" />
                            <span className="text-base font-semibold">AI 스티커 생성기</span>
                        </div>

                        {/* 회전 애니메이션이 적용된 아이콘 */}
                        <ChevronDown
                            className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${
                                showStickerGenerator ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                    </button>

                    {/* 펼쳐지는 컨텐츠 영역 */}
                    <div
                        className={`grid transition-all duration-300 ease-in-out ${
                            showStickerGenerator ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                        }`}
                    >
                        <div className="overflow-hidden">
                            <div className="p-5 border-t border-gray-100 bg-gray-50/50">
                                <StickerGeneratorForm channelId={selectedChannel.channelId}
                                                      setGeneratedImage={setStickerPreview} />
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 p-4">
                        {stickerPreview ? (
                            <>
                                <img
                                    src={stickerPreview}
                                    alt="스티커 이미지"
                                    className="w-[600px] h-[400px] object-contain rounded-xl shadow-sm bg-white"
                                />
                            </>
                        ) : (
                            <div className="w-[600px] h-[400px] flex flex-col items-center justify-center text-gray-400">
                                <ImageIcon size={64} className="mb-4 opacity-20" />
                                <span className="font-medium text-lg text-gray-500">등록된 스티커 이미지가 없습니다.</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans text-gray-900 pb-12">
            {!selectedChannel ? renderMainTable() : renderDetailView()}


            {isPayModalOpen && payingChannel && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-200">
                        <div className="p-6 border-b border-gray-50 flex justify-between items-center">
                            <div className="flex items-center gap-2 text-indigo-600 font-black">
                                <Wallet size={20} />
                                <span>수동 결제 진행</span>
                            </div>
                            <button onClick={() => setIsPayModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-8">
                            <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                                <p className="text-sm text-gray-500 mb-1 font-medium">{payingChannel.channelName}</p>
                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">청구 항목 내역</h3>
                                <div className="space-y-2 border-b border-gray-200 pb-4 mb-4">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 font-medium">기본 월 비용</span>
                                        <span className="font-bold text-gray-900">{payingChannel.monthlyFee?.toLocaleString() || 0}원</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 font-medium">메시지 이용료</span>
                                        <span className="font-bold text-gray-900">{payingChannel.msgFee?.toLocaleString() || 0}원</span>
                                    </div>
                                </div>
                                <div className="flex justify-center items-center">
                                    <span className="w-full font-black text-gray-800">최종 결제 금액</span>

                                    <input
                                        type="number"
                                        value={payingPrice}
                                        onChange={(e) => setPayingPrice(e.target.value)}
                                        className="font-black text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-blue-500"
                                        autoFocus
                                    />
                                    <span className="font-black text-blue-600 px-2">원</span>
                                </div>
                            </div>

                            <p className="text-sm text-center text-gray-500 mb-8 leading-relaxed font-medium">
                                위 금액은 등록된 기본 결제 수단으로 즉시 청구됩니다.<br/>
                                진행하시겠습니까?
                            </p>

                            <div className="flex gap-3">
                                <button onClick={() => setIsPayModalOpen(false)} className="flex-1 py-4 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-2xl transition-colors">닫기</button>
                                <button
                                    onClick={handleManualPayment}
                                    className="flex-[2] py-4 bg-indigo-600 text-white rounded-2xl font-black hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
                                >
                                    수동 결제하기
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}